import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { API } from '../components/five-star/stars';

export default function FiveStarCriteriaPage() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => axios.get(`${API}/five-star/criteria`).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const body = { ...edit, weight: parseFloat(String(edit.weight ?? 1).replace(',', '.')) || 1 };
      if (edit.id) await axios.put(`${API}/five-star/criteria/${edit.id}`, body); else await axios.post(`${API}/five-star/criteria`, body);
      setEdit(null); load(); toast.success('Kaydedildi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
  };
  const remove = async (c) => {
    if (!window.confirm(`"${c.name}" silinsin mi?`)) return;
    const { data } = await axios.delete(`${API}/five-star/criteria/${c.id}`);
    toast.success(data.deactivated ? 'Geçmiş denetimlerde kullanıldığı için pasife alındı' : 'Silindi'); load();
  };
  const move = async (i, d) => {
    const ids = rows.map(r => r.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    await axios.put(`${API}/five-star/criteria-order`, { ids }); load();
  };
  const toggle = async (c) => { await axios.put(`${API}/five-star/criteria/${c.id}`, { ...c, is_active: !c.is_active }); load(); };
  return (
    <div className="space-y-6" data-testid="five-star-criteria-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><ListChecks className="h-6 w-6" />Five Star Başlıkları</h1>
          <p className="text-sm text-slate-500">Denetimde puanlanacak ana başlıklar. Sıra, denetim ekranındaki ve PDF'teki sırayı belirler. Ağırlık, başlığın puana etkisidir; değişiklik yalnızca yeni denetimlere uygulanır.</p>
        </div>
        <Button onClick={() => setEdit({ name: '', description: '', is_active: true, weight: 1 })} data-testid="criteria-add-btn"><Plus className="mr-1 h-4 w-4" />Başlık Ekle</Button>
      </div>
      <Card><CardContent className="divide-y p-0">
        {rows.map((c, i) => (
          <div key={c.id} className={`flex items-center gap-3 px-4 py-3 ${c.is_active ? '' : 'opacity-50'}`} data-testid={`criteria-row-${c.id}`}>
            <span className="w-6 text-center text-sm font-semibold text-slate-400">{i + 1}</span>
            <div className="min-w-0 flex-1"><p className="font-medium text-slate-900">{c.name}</p>{c.description && <p className="truncate text-xs text-slate-500">{c.description}</p>}</div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${(c.weight ?? 1) !== 1 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`} title="Puana etkisi" data-testid={`criteria-weight-${c.id}`}>Ağırlık x{c.weight ?? 1}</span>
            <Switch checked={c.is_active} onCheckedChange={() => toggle(c)} title={c.is_active ? 'Aktif' : 'Pasif'} data-testid={`criteria-active-${c.id}`} />
            <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`criteria-up-${c.id}`}><ArrowUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" disabled={i === rows.length - 1} onClick={() => move(i, 1)} data-testid={`criteria-down-${c.id}`}><ArrowDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setEdit(c)} data-testid={`criteria-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(c)} data-testid={`criteria-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {rows.length === 0 && <p className="p-8 text-center text-sm text-slate-500">Henüz başlık yok</p>}
      </CardContent></Card>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="criteria-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? 'Başlığı Düzenle' : 'Yeni Başlık'}</DialogTitle></DialogHeader>
          <Input value={edit?.name || ''} onChange={e => setEdit({ ...edit, name: e.target.value })} placeholder="ör. KKD, İskele, Merdiven" data-testid="criteria-name" />
          <Input value={edit?.description || ''} onChange={e => setEdit({ ...edit, description: e.target.value })} placeholder="Kısa açıklama (isteğe bağlı)" data-testid="criteria-description" />
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-700">Ağırlık</label>
            <Input type="number" min="0.5" max="10" step="0.5" value={edit?.weight ?? 1} onChange={e => setEdit({ ...edit, weight: e.target.value })} className="w-24" data-testid="criteria-weight" />
            <span className="text-xs text-slate-500">1 = normal, 2 = iki kat etkili. Personel puanı ağırlıklı ortalamayla hesaplanır.</span>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Vazgeç</Button><Button onClick={save} data-testid="criteria-save">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
